import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  FaClipboardList, FaPlus, FaChartBar, FaPaperclip, FaHistory,
  FaUser, FaClock, FaCalendarAlt, FaTag, FaFolderOpen, FaUpload,
  FaPaperclip as FaPaperclipIcon, FaFileAlt, FaEye, FaDownload,
  FaCheckCircle, FaPlay, FaSave, FaEdit, FaArrowLeft, FaCircle, FaLock,
  FaInfoCircle, FaTasks, FaFileAlt as FaFileAltIcon, FaArrowRight,
  FaExclamationTriangle, FaSpinner, FaTimes
} from 'react-icons/fa';
import './StoreManagementWorkflow.css';

// Store Management Workflow Definition
const storeManagementWorkflow = {
  id: 'store_management',
  name: 'Store Management Workflow',
  description: 'Complete end-to-end store management process from purchase request to final inventory reconciliation',
  steps: [
    {
      id: 'SM1',
      label: 'Submit Purchase Request',
      description: 'User enters vendor name, items, quantities, and justification for purchase.',
      order: 1,
      role: 'Procurement',
      requiredInputs: {
        textFields: ['vendorName', 'itemsRequested', 'justification'],
        checklist: ['Supplier vetted', 'Prices validated'],
        fileUpload: ['optionalRequisitionDocument']
      },
      nextStep: 'SM2'
    },
    {
      id: 'SM2',
      label: 'Approve Purchase Request',
      description: 'Manager or finance reviews and approves the request.',
      order: 2,
      role: 'Finance',
      requiredInputs: {
        checklist: ['Budget confirmed', 'Compliance checked'],
        fileUpload: ['approvalProof']
      },
      nextStep: 'SM3'
    },
    {
      id: 'SM3',
      label: 'Create and Send Purchase Order',
      description: 'Procurement generates and sends official PO to vendor.',
      order: 3,
      role: 'Procurement',
      requiredInputs: {
        textFields: ['purchaseOrderNumber', 'sendDate'],
        fileUpload: ['purchaseOrderDocument']
      },
      nextStep: 'SM4'
    },
    {
      id: 'SM4',
      label: 'Receive Goods',
      description: 'Store team receives shipment, verifies quantity and condition.',
      order: 4,
      role: 'Store',
      requiredInputs: {
        checklist: ['Item count verified', 'No visible damage'],
        fileUpload: ['goodsReceipt']
      },
      nextStep: 'SM5'
    },
    {
      id: 'SM5',
      label: 'Inspect Goods',
      description: 'Store performs quality inspection and records findings.',
      order: 5,
      role: 'Store',
      requiredInputs: {
        checklist: ['Inspection passed'],
        fileUpload: ['inspectionReport']
      },
      nextStep: 'SM6'
    },
    {
      id: 'SM6',
      label: 'Match Invoice',
      description: 'Finance compares vendor invoice with order and received goods.',
      order: 6,
      role: 'Finance',
      requiredInputs: {
        checklist: ['Items & prices match', 'No discrepancies'],
        fileUpload: ['supplierInvoice']
      },
      nextStep: 'SM7'
    },
    {
      id: 'SM7',
      label: 'Approve Payment',
      description: 'Finance approves invoice for payment based on matching.',
      order: 7,
      role: 'Finance',
      requiredInputs: {
        fileUpload: ['paymentApproval']
      },
      nextStep: 'SM8'
    },
    {
      id: 'SM8',
      label: 'Record Payment',
      description: 'Finance logs the payment made to the vendor.',
      order: 8,
      role: 'Finance',
      requiredInputs: {
        textFields: ['paymentDate', 'paymentReference'],
        fileUpload: ['paymentProof']
      },
      nextStep: 'SM9'
    },
    {
      id: 'SM9',
      label: 'Update Inventory Records',
      description: 'Store updates stock balances to reflect received items.',
      order: 9,
      role: 'Store',
      requiredInputs: {
        checklist: ['Stock entry updated in system'],
        fileUpload: ['inventoryUpdateEvidence']
      },
      nextStep: 'SM10'
    },
    {
      id: 'SM10',
      label: 'Handle Stock Adjustments',
      description: 'Record and justify any transfers, damages, or returns.',
      order: 10,
      role: 'Store',
      requiredInputs: {
        textFields: ['adjustmentReason'],
        fileUpload: ['adjustmentDocument']
      },
      nextStep: 'SM11'
    },
    {
      id: 'SM11',
      label: 'Conduct Inventory Count',
      description: 'Cycle count performed and discrepancies noted.',
      order: 11,
      role: 'Store',
      requiredInputs: {
        checklist: ['Cycle count complete'],
        fileUpload: ['countSheet']
      },
      nextStep: 'SM12'
    },
    {
      id: 'SM12',
      label: 'Report Discrepancies',
      description: 'Summarize any inventory differences and how they were resolved.',
      order: 12,
      role: 'Store',
      requiredInputs: {
        textFields: ['summaryOfDiscrepancies'],
        fileUpload: ['discrepancyReport']
      },
      nextStep: 'SM13'
    },
    {
      id: 'SM13',
      label: 'Finalize Inventory Update',
      description: 'Store posts final adjustments and closes inventory session.',
      order: 13,
      role: 'Store',
      requiredInputs: {
        checklist: ['System balances confirmed'],
        fileUpload: ['inventoryFinalReport']
      },
      nextStep: null
    }
  ]
};

const StoreManagementWorkflow = () => {
  const { user } = useContext(AuthContext);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepData, setStepData] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Fetch workflows on component mount
  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/workflows/my-workflows', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch workflows');
      }
      
      const data = await response.json();
      setWorkflows(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createWorkflow = async (workflowData) => {
    try {
      setLoading(true);
      const response = await fetch('/api/workflows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(workflowData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create workflow');
      }
      
      const data = await response.json();
      setWorkflows(prev => [data.data, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const completeStep = async (workflowId, stepId, stepData) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/workflows/${workflowId}/steps/${stepId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(stepData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete step');
      }
      
      const data = await response.json();
      setWorkflows(prev => prev.map(w => w._id === workflowId ? data.data : w));
      setShowStepModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (workflowId, stepId, files) => {
    try {
      setUploadingFiles(true);
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`/api/workflows/${workflowId}/steps/${stepId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload files');
      }
      
      const data = await response.json();
      return data.data.uploadedFiles;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setUploadingFiles(false);
    }
  };

  const openStepModal = (workflow, step) => {
    setCurrentWorkflow(workflow);
    setCurrentStep(step);
    setStepData({});
    setShowStepModal(true);
  };

  const getStepStatus = (workflow, stepId) => {
    const step = workflow.steps.find(s => s.stepId === stepId);
    return step ? step.status : 'pending';
  };

  const getStepStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FaCheckCircle className="text-green-500" />;
      case 'in_progress':
        return <FaSpinner className="text-blue-500 animate-spin" />;
      case 'blocked':
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaCircle className="text-gray-400" />;
    }
  };

  const getProgressPercentage = (workflow) => {
    const completedSteps = workflow.steps.filter(step => step.status === 'completed').length;
    return Math.round((completedSteps / workflow.steps.length) * 100);
  };

  const canAccessStep = (workflow, step) => {
    if (step.order === 1) return true;
    const previousStep = workflow.steps.find(s => s.order === step.order - 1);
    return previousStep && previousStep.status === 'completed';
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Procurement':
        return 'bg-blue-100 text-blue-800';
      case 'Finance':
        return 'bg-green-100 text-green-800';
      case 'Store':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="store-management-workflow">
      <div className="workflow-header">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <FaClipboardList className="mr-3 text-blue-600" />
              Store Management Workflow
            </h1>
            <p className="text-gray-600 mt-2">
              Complete end-to-end store management process from purchase request to final inventory reconciliation
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
          >
            <FaPlus className="mr-2" />
            New Workflow
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="workflow-grid">
        {workflows.map(workflow => (
          <div key={workflow._id} className="workflow-card">
            <div className="workflow-card-header">
              <h3 className="workflow-title">{workflow.title}</h3>
              <div className="workflow-meta">
                <span className={`status-badge status-${workflow.status}`}>
                  {workflow.status}
                </span>
                <span className={`priority-badge priority-${workflow.priority}`}>
                  {workflow.priority}
                </span>
              </div>
            </div>

            <div className="workflow-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${getProgressPercentage(workflow)}%` }}
                ></div>
              </div>
              <span className="progress-text">
                {getProgressPercentage(workflow)}% Complete
              </span>
            </div>

            <div className="workflow-steps">
              {storeManagementWorkflow.steps.map(step => {
                const stepStatus = getStepStatus(workflow, step.id);
                const canAccess = canAccessStep(workflow, step);
                
                return (
                  <div 
                    key={step.id} 
                    className={`step-item ${stepStatus} ${!canAccess ? 'locked' : ''}`}
                    onClick={() => canAccess && openStepModal(workflow, step)}
                  >
                    <div className="step-header">
                      <div className="step-icon">
                        {getStepStatusIcon(stepStatus)}
                      </div>
                      <div className="step-info">
                        <h4 className="step-title">{step.label}</h4>
                        <p className="step-description">{step.description}</p>
                      </div>
                      <div className="step-role">
                        <span className={`role-badge ${getRoleColor(step.role)}`}>
                          {step.role}
                        </span>
                      </div>
                    </div>
                    
                    {!canAccess && (
                      <div className="step-locked">
                        <FaLock className="mr-1" />
                        Complete previous step to unlock
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="workflow-actions">
              <button className="action-btn view-btn">
                <FaEye className="mr-1" />
                View Details
              </button>
              <button className="action-btn history-btn">
                <FaHistory className="mr-1" />
                History
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <CreateWorkflowModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createWorkflow}
          loading={loading}
        />
      )}

      {/* Step Completion Modal */}
      {showStepModal && currentWorkflow && currentStep && (
        <StepCompletionModal
          workflow={currentWorkflow}
          step={currentStep}
          onClose={() => setShowStepModal(false)}
          onComplete={completeStep}
          onUpload={uploadFiles}
          loading={loading}
          uploadingFiles={uploadingFiles}
        />
      )}
    </div>
  );
};

// Create Workflow Modal Component
const CreateWorkflowModal = ({ onClose, onCreate, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    tags: []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create New Store Management Workflow</h2>
          <button onClick={onClose} className="modal-close">
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Workflow Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              placeholder="Enter workflow title"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Enter workflow description"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? <FaSpinner className="animate-spin mr-2" /> : <FaPlus className="mr-2" />}
              Create Workflow
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Step Completion Modal Component
const StepCompletionModal = ({ workflow, step, onClose, onComplete, onUpload, loading, uploadingFiles }) => {
  const [formData, setFormData] = useState({
    textFields: {},
    checkboxes: {},
    comments: '',
    files: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Upload files if any
    let uploadedFiles = [];
    if (formData.files.length > 0) {
      uploadedFiles = await onUpload(workflow._id, step.id, formData.files);
    }

    // Complete the step
    await onComplete(workflow._id, step.id, {
      ...formData,
      fileUploads: uploadedFiles
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      files: Array.from(e.target.files)
    });
  };

  const handleTextFieldChange = (field, value) => {
    setFormData({
      ...formData,
      textFields: {
        ...formData.textFields,
        [field]: value
      }
    });
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData({
      ...formData,
      checkboxes: {
        ...formData.checkboxes,
        [field]: checked
      }
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h2>Complete Step: {step.label}</h2>
          <button onClick={onClose} className="modal-close">
            <FaTimes />
          </button>
        </div>
        
        <div className="step-info">
          <p className="step-description">{step.description}</p>
          <div className="step-role">
            <span className={`role-badge ${getRoleColor(step.role)}`}>
              {step.role}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Text Fields */}
          {step.requiredInputs.textFields && step.requiredInputs.textFields.map(field => (
            <div key={field} className="form-group">
              <label>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} *</label>
              <input
                type="text"
                value={formData.textFields[field] || ''}
                onChange={(e) => handleTextFieldChange(field, e.target.value)}
                required
                placeholder={`Enter ${field}`}
              />
            </div>
          ))}

          {/* Checkboxes */}
          {step.requiredInputs.checklist && step.requiredInputs.checklist.map(field => (
            <div key={field} className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.checkboxes[field] || false}
                  onChange={(e) => handleCheckboxChange(field, e.target.checked)}
                  required
                />
                <span className="checkbox-text">{field}</span>
              </label>
            </div>
          ))}

          {/* File Upload */}
          {step.requiredInputs.fileUpload && step.requiredInputs.fileUpload.length > 0 && (
            <div className="form-group">
              <label>Upload Files *</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="file-input"
                />
                <div className="file-upload-text">
                  <FaUpload className="mr-2" />
                  Click to upload files (PDF, DOC, DOCX, JPG, PNG)
                </div>
              </div>
              {formData.files.length > 0 && (
                <div className="uploaded-files">
                  {formData.files.map((file, index) => (
                    <div key={index} className="file-item">
                      <FaFileAlt className="mr-2" />
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="form-group">
            <label>Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData({...formData, comments: e.target.value})}
              placeholder="Add any additional comments"
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading || uploadingFiles}>
              {loading ? (
                <FaSpinner className="animate-spin mr-2" />
              ) : (
                <FaCheckCircle className="mr-2" />
              )}
              Complete Step
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function for role colors (moved outside component)
const getRoleColor = (role) => {
  switch (role) {
    case 'Procurement':
      return 'bg-blue-100 text-blue-800';
    case 'Finance':
      return 'bg-green-100 text-green-800';
    case 'Store':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default StoreManagementWorkflow;
